import {
  StructuredInfo,
  StructuredInfoFormData,
  StructuredInfoOperationRequest,
  PaginatedStructuredInfoResponse,
  MonoXOneConfigForDB,
  MonoXOneMapping,
} from "@/types/structured-info";
import { getCurrentUser } from "aws-amplify/auth";

/**
 * 認証ヘッダーを取得
 */
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    await getCurrentUser(); // ユーザーが存在することを確認
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      console.log("🔐 structured-info.ts - idToken取得成功");
      return {
        "Content-Type": "application/json",
        "Authorization": idToken,
      };
    } else {
      console.warn("🔐 structured-info.ts - idToken取得失敗");
      return {
        "Content-Type": "application/json",
      };
    }
  } catch (error) {
    console.error("🔐 structured-info.ts - 認証ヘッダー取得エラー:", error);
    return {
      "Content-Type": "application/json",
    };
  }
};

/**
 * API呼び出しの共通処理
 */
const callApi = async (requestData: StructuredInfoOperationRequest) => {
  console.log("🔍 callApi - リクエストデータ:", requestData);

  try {
    console.log(
      "🔍 callApi - API呼び出し開始: /api/structuredInformationHandling"
    );

    const headers = await getAuthHeaders();
    console.log("🔐 structured-info.ts - リクエストヘッダー:", headers);
    console.log("🔐 structured-info.ts - Authorizationヘッダー存在:", !!headers.Authorization);
    console.log("🔐 structured-info.ts - Authorizationヘッダー長さ:", headers.Authorization?.length || 0);

    const response = await fetch("/api/structuredInformationHandling", {
      method: "POST",
      headers,
      body: JSON.stringify(requestData),
    });

    console.log(
      "🔍 callApi - レスポンス状態:",
      response.status,
      response.statusText
    );
    console.log(
      "🔍 callApi - レスポンスヘッダー:",
      Object.fromEntries(response.headers.entries())
    );

    const data = await response.json();
    console.log("🔍 callApi - レスポンスデータ:", data);

    // データ取得操作で403/404エラーの場合は空データを返す
    if (!response.ok && requestData.operation === "GETALL") {
      if (response.status === 403) {
        console.warn("🚨 403エラー検出 - 権限問題");
        console.warn(
          "🚨 構造化情報取得の権限がありません。空のリストを返します。"
        );
        console.warn("🚨 レスポンスデータ:", data);
        return {
          message: "データの取得に失敗しました",
          data: [],
          totalItems: 0,
          pagination: {
            currentPage: requestData.page || 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: requestData.limit || 12,
          },
        };
      }
      if (response.status === 404) {
        console.warn("🚨 404エラー検出 - データ未存在");
        console.warn(
          "🚨 構造化情報データが見つかりませんでした。空のリストを返します。"
        );
        console.warn("🚨 レスポンスデータ:", data);
        return {
          message: "データが見つかりませんでした",
          data: [],
          totalItems: 0,
          pagination: {
            currentPage: requestData.page || 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: requestData.limit || 12,
          },
        };
      }
    }

    if (!response.ok) {
      throw new Error(data.error || "APIエラーが発生しました");
    }

    return data;
  } catch (error) {
    // データ取得操作でネットワークエラーの場合は空データを返す
    if (error instanceof TypeError && requestData.operation === "GETALL") {
      console.warn(
        "ネットワークエラーまたはAPIが利用できません。空のリストを返します。"
      );
      return {
        message: "データの取得に失敗しました",
        data: [],
        totalItems: 0,
        pagination: {
          currentPage: requestData.page || 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: requestData.limit || 12,
        },
      };
    }
    throw error;
  }
};

/**
 * 構造化情報一覧の取得（ページネーション対応）
 */
export const fetchStructuredInfo = async (
  page: number = 1,
  limit: number = 12,
  search?: string
): Promise<PaginatedStructuredInfoResponse> => {
  console.log("fetchStructuredInfo called with:", { page, limit, search });

  const requestData = {
    operation: "GETALL",
    page,
    limit,
    search,
  };

  console.log("API request data:", requestData);

  try {
    const data = await callApi(requestData);

    console.log("Raw API response:", data);

    // APIエラーレスポンスをチェック
    if (data.error) {
      console.warn("API側でエラーが発生しました:", data.error);
      return {
        items: [],
        pagination: {
          currentPage: page,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: limit,
        },
      };
    }

    // データが存在しない場合の処理
    if (!data.data || !Array.isArray(data.data)) {
      console.info("構造化情報データが見つかりませんでした。");
      return {
        items: [],
        pagination: {
          currentPage: page,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: limit,
        },
      };
    }

    // バックエンドからのページネーション情報を優先
    let paginationInfo;
    if (data.pagination) {
      console.log("Using backend pagination info:", data.pagination);
      paginationInfo = data.pagination;
    } else {
      console.log("Fallback to calculated pagination");
      // フォールバック: 手動計算
      const totalItems = data.totalItems || data.data.length;
      const totalPages = Math.ceil(totalItems / limit);
      paginationInfo = {
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: limit,
      };
    }

    console.log("Final pagination info:", paginationInfo);

    // データにcreated_atフィールドを含めて返す
    const transformedData = data.data.map((item: StructuredInfo) => ({
      id: item.id,
      name: item.name,
      json: item.json,
    }));

    const result = {
      items: transformedData,
      pagination: paginationInfo,
    };

    console.log("fetchStructuredInfo result:", result);

    return result;
  } catch (error) {
    console.error("構造化情報取得エラー:", error);

    // ネットワークエラーやAPIエラーの場合も空データを返す（サービス継続のため）
    console.warn(
      "構造化情報の取得に失敗しましたが、アプリケーションを継続します。"
    );
    return {
      items: [],
      pagination: {
        currentPage: page,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: limit,
      },
    };
  }
};

/**
 * 構造化情報一覧の取得（後方互換用）
 */
export const fetchAllStructuredInfo = async (): Promise<StructuredInfo[]> => {
  try {
    const result = await fetchStructuredInfo(1, 1000); // 大きなlimitで全件取得
    return result.items;
  } catch (error) {
    console.error("全構造化情報取得エラー:", error);
    console.warn(
      "構造化情報の取得に失敗しましたが、アプリケーションを継続します。"
    );
    return [];
  }
};

/**
 * 新しい構造化情報の追加
 */
export const addStructuredInfo = async (
  formData: StructuredInfoFormData
): Promise<void> => {
  // バリデーションチェック
  if (!formData.name || !formData.json) {
    throw new Error("名前とJSON内容は必須です");
  }

  await callApi({
    operation: "REGIST",
    name: formData.name,
    json: formData.json,
  });
};

/**
 * 既存の構造化情報の編集
 */
export const updateStructuredInfo = async (
  id: string,
  formData: StructuredInfoFormData
): Promise<void> => {
  // バリデーションチェック
  if (!id) {
    throw new Error("構造化情報IDが必要です");
  }
  if (!formData.name || !formData.json) {
    throw new Error("名前とJSON内容は必須です");
  }

  await callApi({
    operation: "UPDATE",
    id: id,
    name: formData.name,
    json: formData.json,
  });
};

/**
 * 構造化情報の削除
 */
export const deleteStructuredInfo = async (id: string): Promise<void> => {
  // バリデーションチェック
  if (!id) {
    throw new Error("構造化情報IDが必要です");
  }

  await callApi({
    operation: "DELETE",
    id: id,
  });
};

/**
 * MONO-X One連携設定の保存
 * structured_informationテーブルのmono_x_one_configフィールドを更新
 */
export const saveMonoXOneConfig = async (
  structuredInfoId: string,
  config: Record<string, unknown>
): Promise<void> => {
  console.log("🚀 saveMonoXOneConfig開始");
  console.log("🚀 構造化情報ID:", structuredInfoId);
  console.log("🚀 保存する設定:", config);

  // バリデーションチェック
  if (!structuredInfoId) {
    throw new Error("構造化情報IDが必要です");
  }
  if (!config) {
    throw new Error("MONO-X One設定が必要です");
  }

  try {
    const requestData = {
      operation: "MONO_X_ONE_SAVE_CONFIG",
      id: structuredInfoId,
      json: config,
    };

    console.log("🚀 APIリクエストデータ:", requestData);

    const result = await callApi(requestData);

    console.log("🚀 saveMonoXOneConfig - API成功レスポンス:", result);

    return result;
  } catch (error) {
    console.error("🚀 saveMonoXOneConfig - APIエラー:", error);
    throw error;
  }
};

/**
 * MONO-X One連携設定の取得
 */
export const getMonoXOneConfig = async (
  structuredInfoId: string
): Promise<MonoXOneConfigForDB | MonoXOneMapping | null> => {
  // バリデーションチェック
  if (!structuredInfoId) {
    throw new Error("構造化情報IDが必要です");
  }

  console.log('🔍 getMonoXOneConfig開始:', structuredInfoId);

  try {
    const requestData = {
      operation: "MONO_X_ONE_GET_CONFIG",
      id: structuredInfoId,
    };
    console.log('🔍 getMonoXOneConfig API呼び出し:', requestData);
    
    const response = await callApi(requestData);
    console.log('🔍 getMonoXOneConfig API応答:', {
      hasData: !!response.data,
      data: response.data,
      fullResponse: response
    });

    return response.data || null;
  } catch (error: unknown) {
    console.error('🔍 getMonoXOneConfig エラー:', {
      structuredInfoId,
      error,
      message: error instanceof Error ? error.message : String(error)
    });
    
    // 404の場合は設定が存在しないことを示すのでnullを返す
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("not found") || errorMessage.includes("404")) {
      console.log('📝 404エラー検出: 設定が存在しないためnullを返します');
      return null;
    }
    // その他のエラーは再throw
    console.error('🚨 その他のエラーを再throw:', errorMessage);
    throw error;
  }
};

/**
 * MONO-X One連携設定の削除
 */
export const deleteMonoXOneConfig = async (
  structuredInfoId: string
): Promise<void> => {
  // バリデーションチェック
  if (!structuredInfoId) {
    throw new Error("構造化情報IDが必要です");
  }

  await callApi({
    operation: "MONO_X_ONE_DELETE_CONFIG",
    id: structuredInfoId,
  });
};

/**
 * 全ての構造化情報のMONO-X One設定を取得し、使用済みページ情報を返す
 */
export const getAllMonoXOneConfigs = async (): Promise<{
  usedPages: Array<{
    structuredInfoId: string;
    structuredInfoName: string;
    tenantName: string;
    siteId: number;
    siteName: string;
    contentId: number;
    contentName: string;
    pageId: number;
    pageName: string;
  }>;
}> => {
  try {
    // 全ての構造化情報を取得
    const allStructuredInfo = await fetchAllStructuredInfo();
    
    const usedPages: Array<{
      structuredInfoId: string;
      structuredInfoName: string;
      tenantName: string;
      siteId: number;
      siteName: string;
      contentId: number;
      contentName: string;
      pageId: number;
      pageName: string;
    }> = [];

    // 各構造化情報のMONO-X One設定を確認
    for (const structuredInfo of allStructuredInfo) {
      try {
        const config = await getMonoXOneConfig(structuredInfo.id);
        if (config) {
          // 新形式の設定
          if ('targetSelection' in config && config.targetSelection) {
            usedPages.push({
              structuredInfoId: config.structuredInfoId,
              structuredInfoName: structuredInfo.name,
              tenantName: config.tenantName,
              siteId: config.targetSelection.siteId,
              siteName: config.targetSelection.siteName,
              contentId: config.targetSelection.contentId,
              contentName: config.targetSelection.contentName,
              pageId: config.targetSelection.pageId,
              pageName: config.targetSelection.pageName,
            });
          }
          // 旧形式の設定
          else if ('pageId' in config) {
            usedPages.push({
              structuredInfoId: config.structuredInfoId,
              structuredInfoName: structuredInfo.name,
              tenantName: config.tenantName,
              siteId: config.siteId || 0,
              siteName: config.siteName || '',
              contentId: config.contentId || 0,
              contentName: config.contentName || '',
              pageId: config.pageId,
              pageName: config.pageName || '',
            });
          }
        }
      } catch (error) {
        // 個別の設定取得エラーは無視して続行
        console.warn(`構造化情報 ${structuredInfo.id} のMONO-X One設定取得をスキップ:`, error);
      }
    }

    return { usedPages };
  } catch (error) {
    console.error('全MONO-X One設定取得エラー:', error);
    // エラー時は空の配列を返す
    return { usedPages: [] };
  }
};

/**
 * IDから構造化情報を取得
 */
export const getStructuredInfoById = async (
  id: string
): Promise<StructuredInfo | null> => {
  // バリデーションチェック
  if (!id) {
    throw new Error("構造化情報IDが必要です");
  }

  try {
    const response = await callApi({
      operation: "GET",
      id: id,
    });

    // データが存在しない場合
    if (!response.data) {
      return null;
    }

    return response.data;
  } catch (error: unknown) {
    // 404の場合は設定が存在しないことを示すのでnullを返す
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("not found") || errorMessage.includes("404")) {
      return null;
    }
    // その他のエラーは再throw
    throw error;
  }
};
